import { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#121210] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-emerald-500/50 focus:shadow-[0_0_15px_rgba(16,185,129,0.1)] transition-all duration-200";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="forgot-password-page" className="pt-28 md:pt-16 min-h-screen flex items-center justify-center relative overflow-hidden">
      <Seo title="Forgot password — HighRank" siteName="HighRank" description="Reset your HighRank password." />
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[30rem] h-[18rem] rounded-full bg-emerald-500/10 blur-[110px]" />

      <FadeUp className="relative w-full max-w-md px-4 py-10">
        <div className="text-center mb-8">
          <Link to="/" data-testid="forgot-logo" className="inline-flex mb-6" aria-label="HighRank.space home">
            <Logo />
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-slate-100">
            Forgot password
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Enter your email and we'll send a reset link.
          </p>
        </div>

        {submitted ? (
          <div
            data-testid="forgot-password-success"
            className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-7 text-center space-y-4"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-slate-200 font-medium">Check your inbox</p>
            <p className="text-sm text-slate-500">
              If <span className="text-slate-300">{email}</span> is registered, you'll receive a password reset link shortly.
            </p>
            <Link
              to="/auth"
              data-testid="forgot-back-to-login"
              className="inline-block mt-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-password-form" className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-7 space-y-4">
            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-300 mb-2">Email address</label>
              <input
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className={inputCls}
              />
            </div>
            {error && (
              <p data-testid="forgot-password-error" className="text-sm text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="forgot-password-submit"
              disabled={submitting}
              className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold rounded-full py-3 transition-all duration-200 hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]"
            >
              {submitting ? "Sending…" : "Send reset link"}
            </button>
            <p className="text-center text-sm text-slate-600">
              Remember it?{" "}
              <Link to="/auth" data-testid="forgot-signin-link" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                Sign in
              </Link>
            </p>
          </form>
        )}
      </FadeUp>
    </main>
  );
}
